import React from 'react';
import { Typography } from 'antd';
import { VisualSelectOption } from '../../../../../types/templates';
import { SLOT_STYLES } from '../../shared/slot-styles';
import { MediaImage } from '../../shared/media-image';

const { Text } = Typography;

interface SelectionSlotProps {
  /** Visual color scheme: 1 (blue) or 2 (orange). For single-slot use, pass 1. */
  slot: 1 | 2;
  /** Placeholder label shown when the slot is empty */
  label: string;
  /** The currently selected option */
  option: VisualSelectOption | undefined;
  /** Callback when the user clears the slot */
  onClear: () => void;
}

/**
 * A filled/empty slot that displays the currently selected option.
 * When filled: shows image (with optional backgroundColor), label, and a clear (×) button.
 * When empty: shows just the placeholder label in neutral styling.
 */
export const SelectionSlot: React.FC<SelectionSlotProps> = ({ slot, label, option, onClear }) => {
  const styles = SLOT_STYLES[slot];
  const filled = !!option;

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: filled ? styles.background : 'var(--color-background-secondary, #f5f5f5)',
        border: filled ? styles.border : '0.5px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: '7px 10px',
        minHeight: 44,
        transition: 'all 0.12s'
      }}
    >
      {filled && option?.imageUrl && (
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 4,
            flexShrink: 0,
            background: option.backgroundColor ?? 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}
        >
          <MediaImage
            src={option.imageUrl}
            alt={String(option.label ?? option.value)}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>
      )}

      <div style={{ flex: 1, overflow: 'hidden' }}>
        <Text
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: filled ? styles.nameColor : undefined,
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {filled ? String(option!.label ?? option!.value) : label}
        </Text>
      </div>

      {filled && (
        <button
          onClick={onClear}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            padding: '0 2px',
            color: styles.nameColor,
            flexShrink: 0
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};
